import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Clock,
  User,
  UserPlus,
  Stethoscope,
  Building2,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Phone,
  Sparkles,
  ClipboardList,
  Save,
} from 'lucide-react';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { joinFullName } from '../../utils/patientName';
import { SendSmsConfirmModal } from '../../components/ui/SendSmsConfirmModal';
import type { ConsultationTimeRange, Doctor } from '../../services/httpServices/doctorService';
import {
  usePatients,
  useDoctors,
  useDepartments,
  useCreateAppointment,
  useUpdateAppointment,
  useAppointment,
} from '../../services/httpServices/queries';
import type { Appointment, CreateAppointmentDto } from '../../types/appointment';

/* ─────────────────────────── date helpers ─────────────────────────── */

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const WEEKDAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatLongDate(dateKey: string): string {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T00:00:00`);
  return `${WEEKDAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Sentinel day value meaning the doctor is available every weekday. */
const EVERYDAY = 'Everyday';

const isEveryday = (day: string | undefined) =>
  (day || '').toLowerCase() === EVERYDAY.toLowerCase();

const hasTimeRanges = (entry: { timeRanges?: unknown[] } | undefined) =>
  (entry?.timeRanges?.length ?? 0) > 0;

/**
 * Consultation availability for the requested date. A schedule configured for
 * the exact weekday always overrides the "Everyday" schedule; "Everyday" only
 * applies to weekdays that have no schedule of their own.
 */
function doctorAvailabilityFor(doctor: Doctor | undefined, dateKey: string) {
  if (!doctor?.consultationAvailability?.length) return undefined;
  const weekday = WEEKDAYS[new Date(`${dateKey}T00:00:00`).getDay()];
  return (
    doctor.consultationAvailability.find(
      (entry) => !isEveryday(entry.day) && (entry.day || '').toLowerCase() === weekday.toLowerCase() && hasTimeRanges(entry),
    ) ??
    doctor.consultationAvailability.find((entry) => isEveryday(entry.day) && hasTimeRanges(entry))
  );
}

/**
 * Builds the "Consultation availability" summary. When an "Everyday" schedule is
 * configured it is shown as covering the days that are not overridden by their
 * own weekday schedule, e.g. "Everyday (except Sat) 09:00–17:00 · Saturday 09:00–12:00".
 */
function buildScheduleSummary(
  doctor: Doctor | undefined,
): Array<{ label: string; ranges: string }> {
  const entries = (doctor?.consultationAvailability ?? []).filter(hasTimeRanges);
  const everyday = entries.find((entry) => isEveryday(entry.day));
  const specificDays = entries.filter((entry) => !isEveryday(entry.day));

  const rangesOf = (entry: (typeof entries)[number]) =>
    entry.timeRanges.map((range) => `${range.from}–${range.to}`).join(', ');

  if (!everyday) {
    return specificDays.map((entry) => ({ label: entry.day, ranges: rangesOf(entry) }));
  }

  const overriddenDays = specificDays
    .map((entry) => entry.day)
    .filter((day) => WEEKDAYS.some((weekday) => weekday.toLowerCase() === (day || '').toLowerCase()))
    .sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));

  const label = overriddenDays.length
    ? `${EVERYDAY} (except ${overriddenDays.map((day) => day.slice(0, 3)).join(', ')})`
    : EVERYDAY;

  return [
    { label, ranges: rangesOf(everyday) },
    ...specificDays.map((entry) => ({ label: entry.day, ranges: rangesOf(entry) })),
  ];
}

function isDoctorAvailableOn(doctor: Doctor | undefined, dateKey: string): boolean {
  if (!doctor) return false;
  return (doctorAvailabilityFor(doctor, dateKey)?.timeRanges?.length ?? 0) > 0;
}

/* ─────────────────────────── component ─────────────────────────── */

export function AppointmentForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { data, isLoading, error } = useAppointment(id ?? '');

  if (isEdit) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    if (error || !data?.data) {
      return (
        <div className="max-w-md mx-auto py-16 text-center space-y-4">
          <AlertCircle className="w-10 h-10 text-danger mx-auto" />
          <p className="text-text font-medium">Appointment not found</p>
          <button
            onClick={() => navigate('/appointments')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            Back to appointments
          </button>
        </div>
      );
    }

    return <AppointmentFormView key={id} appointment={data.data} />;
  }

  return <AppointmentFormView key="new" />;
}

function AppointmentFormView({ appointment }: { appointment?: Appointment }) {
  const navigate = useNavigate();
  const isEdit = Boolean(appointment);

  const [patientMode, setPatientMode] = useState<'walk-in' | 'existing'>(
    appointment?.patientId ? 'existing' : 'walk-in',
  );
  const [patientId, setPatientId] = useState(appointment?.patientId ?? '');
  const [patientName, setPatientName] = useState(appointment?.patientName ?? '');
  const [patientMobile, setPatientMobile] = useState(appointment?.patientMobile ?? '');
  const [departmentId, setDepartmentId] = useState(appointment?.departmentId ?? '');
  const [doctorId, setDoctorId] = useState(appointment?.doctorId ?? '');
  const [date, setDate] = useState(
    appointment?.date ? String(appointment.date).slice(0, 10) : '',
  );
  const [time, setTime] = useState(appointment?.time ?? '');
  const [appointmentType, setAppointmentType] = useState(appointment?.appointmentType || '');
  const [notes, setNotes] = useState(appointment?.notes ?? '');
  const [viewMonth, setViewMonth] = useState(() => {
    const base = appointment?.date
      ? new Date(`${String(appointment.date).slice(0, 10)}T00:00:00`)
      : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const [pendingPayload, setPendingPayload] = useState<CreateAppointmentDto | null>(null);
  const [showSmsModal, setShowSmsModal] = useState(false);

  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments();
  const { data: doctorsData, isLoading: doctorsLoading, isFetching: doctorsFetching } =
    useDoctors(1, 100, { departmentId: departmentId || undefined, status: 'active' });
  const { data: patientsData, isLoading: patientsLoading } = usePatients(1, 100, {
    isActive: true,
  });
  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const saving = createAppointment.isPending || updateAppointment.isPending;

  const departments = departmentsData?.data || [];
  const doctors = doctorsData?.data || [];
  const patients = patientsData?.data || [];

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.id === doctorId),
    [doctors, doctorId],
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        value: d.id,
        label: d.code ? `${d.name} (${d.code})` : d.name,
      })),
    [departments],
  );

  const doctorOptions = useMemo(
    () =>
      doctors.map((d) => ({
        value: d.id,
        label: d.designation?.name ? `${d.name} · ${d.designation.name}` : d.name,
      })),
    [doctors],
  );

  const patientOptions = useMemo(
    () =>
      patients.map((p) => ({
        value: p.id,
        label:
          joinFullName(p) ||
          p.registrationNumber ||
          p.mobileNumber ||
          p.id,
      })),
    [patients],
  );

  /* reset date/time whenever the doctor changes (skip the initial hydration) */
  const lastDoctorIdRef = useRef(doctorId);
  useEffect(() => {
    if (lastDoctorIdRef.current === doctorId) return;
    lastDoctorIdRef.current = doctorId;
    setDate('');
    setTime('');
  }, [doctorId]);

  const selectedWindows = useMemo<ConsultationTimeRange[]>(() => {
    if (!date) return [];
    return doctorAvailabilityFor(selectedDoctor, date)?.timeRanges ?? [];
  }, [date, selectedDoctor]);

  /* drop a previously chosen time when the date changes (skip the initial hydration) */
  const lastDateRef = useRef(date);
  useEffect(() => {
    if (lastDateRef.current === date) return;
    lastDateRef.current = date;
    setTime('');
  }, [date]);

  const mobileDigits = patientMobile.replace(/\D/g, '');
  const mobileInvalid = patientMode === 'walk-in' && patientMobile.length > 0 && mobileDigits.length < 6;

  const isWalkIn = patientMode === 'walk-in';
  const hasPatient = isWalkIn ? patientName.trim().length > 0 && mobileDigits.length >= 6 : Boolean(patientId);
  const canSubmit = Boolean(
    hasPatient && departmentId && doctorId && date && time && appointmentType && !mobileInvalid,
  );

  /* ── calendar grid ── */
  const calendarCells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ day: number; dateKey: string; available: boolean; past: boolean } | null> = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    const today = startOfDay(new Date());
    for (let day = 1; day <= daysInMonth; day += 1) {
      const cellDate = new Date(year, month, day);
      const dateKey = toDateKey(cellDate);
      cells.push({
        day,
        dateKey,
        past: cellDate < today,
        available: Boolean(selectedDoctor) && isDoctorAvailableOn(selectedDoctor, dateKey) && cellDate >= today,
      });
    }
    return cells;
  }, [viewMonth, selectedDoctor]);

  const isCurrentViewMonth =
    viewMonth.getFullYear() === new Date().getFullYear() &&
    viewMonth.getMonth() === new Date().getMonth();

  const doctorScheduleSummary = useMemo(
    () => buildScheduleSummary(selectedDoctor),
    [selectedDoctor],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload: CreateAppointmentDto = {
      doctorId,
      departmentId,
      date,
      time,
      notes: notes.trim() || undefined,
      appointmentType: appointmentType || undefined,
      ...(isWalkIn
        ? { patientName: patientName.trim(), patientMobile: patientMobile.trim(), isWalkIn: true }
        : { patientId, isWalkIn: false }),
    };

    // Editing keeps the existing appointment; no confirmation SMS prompt.
    if (isEdit && appointment) {
      updateAppointment.mutate(
        { id: appointment.id, data: payload },
        {
          onSuccess: () => {
            toast.success('Appointment updated');
            navigate('/appointments');
          },
        },
      );
      return;
    }

    // Ask staff whether the patient should receive the confirmation SMS.
    setPendingPayload(payload);
    setShowSmsModal(true);
  };

  const bookAppointment = (sendSms: boolean) => {
    if (!pendingPayload) return;
    createAppointment.mutate(
      { ...pendingPayload, sendSms },
      {
        onSuccess: (response) => {
          setShowSmsModal(false);
          const serial = response?.data?.serialNumber;
          toast.success(
            serial ? `Appointment booked · Serial #${serial}` : 'Appointment booked',
          );
          navigate('/appointments');
        },
      },
    );
  };

  const handleSmsModalOpenChange = (open: boolean) => {
    if (createAppointment.isPending) return;
    setShowSmsModal(open);
    if (!open) setPendingPayload(null);
  };

  const registeredPatient = patientId ? patients.find((p) => p.id === patientId) : undefined;

  /* Patient identity shown read-only when editing. */
  const editPatientName = appointment
    ? joinFullName(appointment.patient || {}) ||
      appointment.patientName ||
      appointment.patient?.registrationNumber ||
      '—'
    : '—';
  const editPatientMobile =
    appointment?.patient?.mobileNumber || appointment?.patientMobile || '';

  const summaryPatientLabel = isEdit
    ? editPatientName
    : isWalkIn
      ? patientName.trim() || 'Walk-in patient'
      : registeredPatient?.registrationNumber ||
        joinFullName(registeredPatient || {}) ||
        '—';
  const summaryPatientSub = isEdit
    ? editPatientMobile || 'No mobile'
    : isWalkIn
      ? patientMobile || 'No mobile'
      : registeredPatient?.mobileNumber || '';
  const summaryDepartment = departments.find((d) => d.id === departmentId)?.name || '—';

  /* Consultation room + floor: prefer the room snapshotted on the appointment. */
  const summaryRoom = appointment?.consultationRoom ?? selectedDoctor?.consultationRoom;
  const summaryRoomLabel = summaryRoom?.name || '—';
  const summaryFloorLabel = summaryRoom?.floor
    ? summaryRoom.floor.name ||
      (summaryRoom.floor.floorNumber && summaryRoom.floor.floorNumber > 0
        ? `Floor ${summaryRoom.floor.floorNumber}`
        : '—')
    : '—';

  /* Serial is assigned by the backend on save; only known when editing. */
  const summarySerial = appointment?.serialNumber
    ? `#${appointment.serialNumber}${appointment.session ? ` · ${appointment.session}` : ''}`
    : '—';

  /*
   * Consultation fee shown on the summary, keyed off the selected appointment
   * type and sourced from the doctor's configured fees. Mirrors the backend's
   * `resolveConsultationFee` bands: report reviews use the report fee, and
   * every other type uses the new-patient rate (emergency/procedure visits are
   * still stamped at the new or follow-up rate server-side).
   */
  const consultationFee = useMemo(() => {
    if (!selectedDoctor) return null;
    const type = appointmentType.trim().toLowerCase();

    if (type === 'report review' || type === 'report check' || type === 'report') {
      return {
        amount: Number(selectedDoctor.consultationFeeReport ?? 0),
        sub: 'Report check rate',
      };
    }

    if (type === 'follow-up' || type === 'followup') {
      return {
        amount: Number(selectedDoctor.consultationFeeFollowup ?? 0),
        sub: 'Within 6 months of last visit',
      };
    }

    return {
      amount: Number(selectedDoctor.consultationFeeNew ?? 0),
      sub:
        type === 'emergency' || type === 'procedure'
          ? 'Charged at the new-patient rate'
          : undefined,
    };
  }, [selectedDoctor, appointmentType]);

  /* Mobile number that a booking confirmation SMS would be sent to. */
  const confirmationMobile = isWalkIn
    ? patientMobile.trim()
    : patients.find((p) => p.id === patientId)?.mobileNumber || '';
  const smsAvailable = confirmationMobile.replace(/\D/g, '').length >= 6;

  /* Appointment type — shown in the Patient section as the last, narrower column. */
  const appointmentTypeField = (
    <>
      <label className="block text-sm font-medium text-text mb-1">Type *</label>
      <select
        value={appointmentType}
        onChange={(e) => setAppointmentType(e.target.value)}
        required
        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="" disabled>
          Select type
        </option>
        <option>New</option>
        <option>Follow-up</option>
        <option>Emergency</option>
        <option>Procedure</option>
        <option>Report Review</option>
      </select>
    </>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/appointments')}
          className="p-2 hover:bg-surface rounded-lg transition-colors cursor-pointer"
          aria-label="Back to appointments"
        >
          <ArrowLeft className="w-5 h-5 text-text" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">
              {isEdit ? 'Edit Appointment' : 'Book Appointment'}
            </h1>
            <p className="text-sm text-text-muted">
              {isEdit
                ? 'Update the appointment schedule, type or notes'
                : 'Walk-in or existing patient · schedule follows the doctor\u2019s consultation availability'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-5">
          {/* ── Patient ── */}
          <section className="bg-surface rounded-2xl border border-border p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-text">Patient</h2>
              </div>
              {!isEdit && (
                <div className="flex bg-background border border-border rounded-lg p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setPatientMode('walk-in')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 ${
                      patientMode === 'walk-in'
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-muted hover:text-text'
                    }`}
                  >
                    <UserPlus size={13} /> Walk-in
                  </button>
                  <button
                    type="button"
                    onClick={() => setPatientMode('existing')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 ${
                      patientMode === 'existing'
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-muted hover:text-text'
                    }`}
                  >
                    <User size={13} /> Existing
                  </button>
                </div>
              )}
            </div>

            {isEdit ? (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Patient</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="text"
                      value={editPatientName}
                      readOnly
                      className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background/60 text-text-muted text-sm cursor-not-allowed"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Mobile Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="text"
                      value={editPatientMobile || '—'}
                      readOnly
                      className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background/60 text-text-muted text-sm cursor-not-allowed"
                    />
                  </div>
                </div>
                <div className="md:w-36">{appointmentTypeField}</div>
                <p className="md:col-span-3 text-xs text-text-muted flex items-center gap-1.5">
                  <Sparkles size={13} className="text-secondary" />
                  The patient on an appointment can&apos;t be changed. Cancel and rebook to use a
                  different patient.
                </p>
              </div>
            ) : patientMode === 'walk-in' ? (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Full Name *</label>
                  <div className="relative">
                    <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="text"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      placeholder="e.g. Rahim Uddin"
                      className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Mobile Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="tel"
                      value={patientMobile}
                      onChange={(e) => setPatientMobile(e.target.value)}
                      placeholder="e.g. 01700000000"
                      className={`w-full pl-9 pr-3 py-2 border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 ${
                        mobileInvalid
                          ? 'border-danger focus:ring-danger'
                          : 'border-border focus:ring-primary'
                      }`}
                    />
                  </div>
                  {mobileInvalid && (
                    <p className="mt-1 text-xs text-danger">Enter a valid mobile number.</p>
                  )}
                </div>
                <div className="md:w-36">{appointmentTypeField}</div>
                <p className="md:col-span-3 text-xs text-text-muted flex items-center gap-1.5">
                  <Sparkles size={13} className="text-secondary" />
                  A walk-in appointment only needs a name and mobile number.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-start">
                <div className="md:col-span-2">
                  <SearchableSelect
                    label="Select registered patient *"
                    labelClassName="block text-sm font-medium text-text mb-1"
                    value={patientId}
                    onChange={setPatientId}
                    options={patientOptions}
                    placeholder="Search by name, UHID or mobile..."
                    loading={patientsLoading}
                  />
                </div>
                <div className="md:w-36">{appointmentTypeField}</div>
              </div>
            )}
          </section>

          {/* ── Department & Doctor ── */}
          <section className="bg-surface rounded-2xl border border-border p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-text">Department &amp; Doctor</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchableSelect
                label="Department *"
                labelClassName="block text-sm font-medium text-text mb-1"
                value={departmentId}
                onChange={(value) => {
                  setDepartmentId(value);
                  setDoctorId('');
                }}
                options={departmentOptions}
                placeholder="Select department..."
                loading={departmentsLoading}
              />
              <SearchableSelect
                label="Doctor *"
                labelClassName="block text-sm font-medium text-text mb-1"
                value={doctorId}
                onChange={setDoctorId}
                options={doctorOptions}
                placeholder={departmentId ? 'Select doctor...' : 'Choose a department first'}
                loading={doctorsLoading || doctorsFetching}
                disabled={!departmentId}
              />
            </div>

            {selectedDoctor && doctorScheduleSummary.length > 0 && (
              <div className="mt-4 rounded-xl bg-primary/5 border border-primary/15 p-3">
                <p className="text-xs font-medium text-primary mb-2 flex items-center gap-1.5">
                  <Stethoscope size={13} /> Consultation availability
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {doctorScheduleSummary.map((item) => (
                    <span
                      key={item.label}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-surface border border-border text-xs text-text"
                    >
                      <span className="font-medium">{item.label}</span>
                      <span className="text-text-muted">{item.ranges}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {selectedDoctor && doctorScheduleSummary.length === 0 && (
              <p className="mt-4 text-xs text-warning flex items-center gap-1.5">
                <AlertCircle size={13} /> This doctor has no consultation hours configured — set
                their consultation availability before booking an appointment.
              </p>
            )}
          </section>

          {/* ── Schedule ── */}
          <section className="bg-surface rounded-2xl border border-border p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-text">Date &amp; Time</h2>
            </div>

            {!selectedDoctor ? (
              <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-xl bg-background/50">
                <Calendar className="w-8 h-8 text-text-muted mb-2" />
                <p className="text-sm text-text-muted">
                  Select a doctor to see their available consultation dates.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Calendar */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <button
                      type="button"
                      disabled={isCurrentViewMonth}
                      onClick={() =>
                        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
                      }
                      className="p-1.5 rounded-lg border border-border text-text-muted hover:text-text disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous month"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-sm font-medium text-text">
                      {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
                      }
                      className="p-1.5 rounded-lg border border-border text-text-muted hover:text-text transition-colors"
                      aria-label="Next month"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {WEEKDAY_SHORT.map((w) => (
                      <span key={w} className="text-[11px] font-medium text-text-muted py-1">
                        {w}
                      </span>
                    ))}
                    {calendarCells.map((cell, index) =>
                      cell ? (
                        <button
                          key={cell.dateKey}
                          type="button"
                          disabled={!cell.available}
                          onClick={() => setDate(cell.dateKey)}
                          title={
                            cell.available
                              ? formatLongDate(cell.dateKey)
                              : cell.past
                                ? 'Past date'
                                : 'Doctor not available'
                          }
                          className={`h-9 rounded-lg text-sm transition-colors ${
                            date === cell.dateKey
                              ? 'bg-primary text-white font-semibold shadow-sm'
                              : cell.available
                                ? 'text-text hover:bg-primary/10 hover:text-primary'
                                : 'text-text-muted/40 cursor-not-allowed'
                          }`}
                        >
                          {cell.day}
                        </button>
                      ) : (
                        <span key={`empty-${index}`} />
                      ),
                    )}
                  </div>
                </div>

                {/* Time */}
                <div className="space-y-3">
                  {date ? (
                    <>
                      <p className="text-xs text-text-muted">
                        Selected: <span className="text-text font-medium">{formatLongDate(date)}</span>
                      </p>
                      <div>
                        <label className="block text-sm font-medium text-text mb-2">
                          Consultation Time *
                        </label>
                        {selectedWindows.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {selectedWindows.map((w) => (
                              <button
                                key={`${w.from}-${w.to}`}
                                type="button"
                                onClick={() => setTime(w.from)}
                                title={w.title ? `${w.title} (${w.from}–${w.to})` : `${w.from}–${w.to}`}
                                className={`px-3.5 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                  time === w.from
                                    ? 'bg-primary text-white border-primary shadow-sm'
                                    : 'bg-background border-border text-text hover:border-primary hover:text-primary'
                                }`}
                              >
                                <span className="block leading-tight">{w.from}</span>
                                {w.title && (
                                  <span
                                    className={`block text-[10px] font-normal leading-tight ${
                                      time === w.from ? 'text-white/80' : 'text-text-muted'
                                    }`}
                                  >
                                    {w.title}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-warning flex items-center gap-1.5">
                            <AlertCircle size={12} /> No consultation time is available for this
                            day.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-text-muted">
                      Pick a date to choose a time.
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* ── Details ── */}
          <section className="bg-surface rounded-2xl border border-border p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-text">Appointment Details</h2>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Additional notes for the doctor or receptionist..."
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </section>
        </div>

        {/* ── Summary ── */}
        <aside className="lg:sticky lg:top-6 space-y-4">
          <div className="bg-surface rounded-2xl border border-border p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-text mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />{' '}
              {isEdit ? 'Appointment Summary' : 'Booking Summary'}
            </h2>
            <dl className="space-y-3 text-sm">
              <SummaryRow
                label="Patient"
                value={summaryPatientLabel}
                sub={summaryPatientSub}
                badge={isWalkIn ? 'Walk-in' : undefined}
              />
              <SummaryRow label="Department" value={summaryDepartment} />
              <SummaryRow label="Doctor" value={selectedDoctor?.name || '—'} />
              <SummaryRow label="Consultation Room" value={summaryRoomLabel} />
              <SummaryRow label="Floor" value={summaryFloorLabel} />
              {isEdit && <SummaryRow label="Serial No." value={summarySerial} />}
              <SummaryRow
                label="Date"
                value={date ? formatLongDate(date) : '—'}
              />
              <SummaryRow
                label="Time"
                value={time || '—'}
              />
              <SummaryRow label="Type" value={appointmentType || '—'} />
              {selectedDoctor && consultationFee && (
                <SummaryRow
                  label="Doctor Consultation Fee"
                  value={`৳${consultationFee.amount.toLocaleString()}`}
                  sub={consultationFee.sub}
                />
              )}
            </dl>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={!canSubmit || saving}
              className="w-full px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : isEdit ? (
                <Save size={16} />
              ) : (
                <Calendar size={16} />
              )}
              {isEdit ? 'Save Changes' : 'Book Appointment'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/appointments')}
              className="w-full px-4 py-2.5 text-sm text-text-muted hover:text-text border border-border rounded-xl transition-colors"
            >
              Cancel
            </button>
          </div>
        </aside>
      </form>

      {!isEdit && (
        <SendSmsConfirmModal
          open={showSmsModal}
          patientName={summaryPatientLabel}
          mobile={confirmationMobile}
          loading={createAppointment.isPending}
          smsAvailable={smsAvailable}
          onSend={() => bookAppointment(true)}
          onSkip={() => bookAppointment(false)}
          onCancel={() => handleSmsModalOpenChange(false)}
        />
      )}
    </div>
  );
}

function SummaryRow({
  label,
  value,
  sub,
  badge,
}: {
  label: string;
  value: string;
  sub?: string;
  badge?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 pb-3 last:border-0 last:pb-0">
      <dt className="text-text-muted shrink-0">{label}</dt>
      <dd className="text-right min-w-0">
        <span className="text-text font-medium break-words inline-flex items-center gap-1.5 justify-end">
          {value}
          {badge && (
            <span className="px-1.5 py-0.5 rounded bg-secondary/10 text-secondary text-[10px] font-semibold uppercase">
              {badge}
            </span>
          )}
        </span>
        {sub && <span className="block text-xs text-text-muted break-words">{sub}</span>}
      </dd>
    </div>
  );
}
